/**
 * Builds the `AssertionIssueLinkContext` that powers "Link issue" on a
 * failing assertion row (see `StepAssertions`) — shared by the flow-designer
 * run panel (`RunDetailsPanel`) and the canonical run detail page
 * (`RunDetailPage`), which previously duplicated this derivation.
 *
 * Filing a *new* issue needs a primary URL: prefer the sandbox instance's
 * live URL (most specific to this run), else the property's base URL.
 * Attaching to an already-open issue doesn't need one.
 *
 * @module hooks/useAssertionIssueLinkContext
 */
import React from 'react';
import { useWorkflowOpenIssues } from './useWorkflowOpenIssues';
import { usePropertySummaries } from './usePropertyMetadata';
import type { AssertionIssueLinkContext } from '../ui/pages/FlowDesigner/runs/StepAssertions';

type RunForIssueLinking = {
  id: string;
  environmentInstance?: { publicUrl: string | null } | null;
};

export function useAssertionIssueLinkContext(
  run: RunForIssueLinking | null | undefined,
  workflowId: string | null,
  propertyId: string | null
): AssertionIssueLinkContext | null {
  const openIssuesQuery = useWorkflowOpenIssues(workflowId);
  const propertySummariesQuery = usePropertySummaries({ enabled: Boolean(propertyId) });
  const propertyBaseUrl =
    propertySummariesQuery.data?.find((p) => p.id === propertyId)?.baseUrl ?? null;

  return React.useMemo<AssertionIssueLinkContext | null>(() => {
    if (!run || !workflowId) return null;
    return {
      workflowId,
      runId: run.id,
      propertyId,
      openIssues: openIssuesQuery.data ?? [],
      primaryUrl: run.environmentInstance?.publicUrl ?? propertyBaseUrl,
    };
  }, [run, workflowId, propertyId, openIssuesQuery.data, propertyBaseUrl]);
}
